import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  ControlCostMissingVariablesDocument,
  ControlCostMissingVariablesQuery,
  ControlCostMissingVariablesQueryVariables,
} from 'src/graphql/_generated';
import { findVariableByToken } from 'src/components/FormulaInput/shared/formula';
import useVariables from 'src/hooks/useVariables/useVariables';
import { useAuth } from 'src/state/auth';
import { showErrorToast } from 'src/components/Toast';
import { getErrorMessage } from 'src/utils/error';
import { logger } from 'src/utils/logger';
import { MissingVariablesModal } from './MissingVariablesModal';
import type { Destination } from './MissingVariablesModal';
import { useSaveMissingVariables } from './useSaveMissingVariables';
import { MissingVariableEntry } from './util';

interface UseControlCostVariableReviewConfig {
  /**
   * Save-failure sink. Omit where the host surface's error line would sit UNDER the
   * review modal's overlay — the toast fallback renders above stacked modals.
   */
  onError?: (err: unknown) => void;
}

export interface ControlCostReviewSpec {
  /** Library template ids (promotion — the BE copies template formulas onto the new controls). */
  frameworkControlIds?: string[];
  /** Existing workspace control ids (their own, possibly user-edited, cost formulas). */
  controlIds?: string[];
  destination: Destination;
  plural: boolean;
}

interface ReviewState extends Pick<ControlCostReviewSpec, 'destination' | 'plural'> {
  entries: MissingVariableEntry[];
  commit: () => Promise<void>;
}

const prettify = (name: string): string => {
  const display = name.replace(/_/g, ' ');
  return display.charAt(0).toUpperCase() + display.slice(1);
};

/**
 * The Missing-Variables review (nudge) core: the BE `controlCostMissingVariables` query
 * reports which variables the given templates'/controls' cost formulas reference that are
 * unpopulated in the workspace — when any, the modal opens first (Save & Proceed populates
 * them, Skip commits anyway) and `commit` runs after; otherwise `commit` runs immediately.
 * Detection is best-effort and NEVER blocks the commit. The consumer renders `reviewModal`
 * in its tree. `useAddControlsWithVariableReview` wraps this for the plain add-to-RMP
 * surfaces; LinkControlsToScenarioModal drives it directly with its own commits.
 */
export const useControlCostVariableReview = ({ onError }: UseControlCostVariableReviewConfig = {}) => {
  const { workspaceId } = useAuth();
  const { variables } = useVariables({});
  const client = useApolloClient();
  const saveMissingVariables = useSaveMissingVariables({});

  const [review, setReview] = useState<ReviewState | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleError = (err: unknown) => {
    if (onError) onError(err);
    else showErrorToast(getErrorMessage(err, 'Something went wrong'));
  };

  const requestWithReview = async (spec: ControlCostReviewSpec, commit: () => Promise<void>) => {
    const { frameworkControlIds, controlIds, destination, plural } = spec;
    const hasIds = !!(frameworkControlIds?.length || controlIds?.length);
    // The modal maps BE variable ids to local variable metadata (currency, scope) —
    // with variables still loading, skip detection rather than risk a review that
    // saves duplicates. The commit itself must never be blocked by a detection failure.
    if (variables && hasIds) {
      try {
        setIsChecking(true);
        const { data } = await client.query<ControlCostMissingVariablesQuery, ControlCostMissingVariablesQueryVariables>(
          {
            query: ControlCostMissingVariablesDocument,
            variables: {
              workspaceId,
              ...(frameworkControlIds?.length ? { frameworkControlIds } : {}),
              ...(controlIds?.length ? { controlIds } : {}),
            },
            fetchPolicy: 'no-cache',
          },
        );
        const entries: MissingVariableEntry[] = (data?.controlCostMissingVariables ?? []).map((missing) => {
          const variable = findVariableByToken(variables, String(missing.id)) ?? null;
          return {
            variable,
            token: String(missing.id),
            displayName: variable ? variable.alias ?? variable.name : prettify(missing.name),
          };
        });

        if (entries.length) {
          setReview({ entries, destination, plural, commit });
          return;
        }
      } catch (err) {
        logger.data.warn('controlCostMissingVariables detection failed — proceeding without the review', err);
      } finally {
        setIsChecking(false);
      }
    }
    await commit();
  };

  const reviewModal = review ? (
    <MissingVariablesModal
      entries={review.entries}
      destination={review.destination}
      plural={review.plural}
      saving={isSaving || isChecking}
      onSaveAndProceed={async (values) => {
        setIsSaving(true);
        try {
          await saveMissingVariables(values);
          const { commit } = review;
          setReview(null);
          await commit();
        } catch (err) {
          handleError(err);
        } finally {
          setIsSaving(false);
        }
      }}
      onSkip={async () => {
        const { commit } = review;
        setReview(null);
        await commit();
      }}
      onClose={() => setReview(null)}
    />
  ) : null;

  return { requestWithReview, reviewModal, isChecking, isSaving };
};
